/**
 * Destek (Support) page — accessible to all authenticated users (admin /
 * staff / customer). Opens a ticket and shows the user's own ticket
 * history below the form. SuperAdmin sees the full inbox elsewhere.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PageLayout } from '@/components/PageLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LifeBuoy, MessageSquare, Send, Clock, CheckCircle2 } from 'lucide-react';

const CATEGORY_LABELS = {
  bug: 'Hata',
  feature: 'Özellik İsteği',
  billing: 'Fatura / Ödeme',
  appointment: 'Randevu',
  other: 'Diğer',
};

const STATUS_LABELS = {
  open: { label: 'Açık', color: 'bg-blue-50 text-blue-700' },
  in_progress: { label: 'İncelemede', color: 'bg-amber-50 text-amber-700' },
  resolved: { label: 'Çözüldü', color: 'bg-green-50 text-green-700' },
  closed: { label: 'Kapalı', color: 'bg-slate-50 text-slate-700' },
};

export default function SupportPage() {
  const API_URL = process.env.REACT_APP_API_URL;
  const [form, setForm] = useState({ subject: '', category: 'other', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [reply, setReply] = useState('');

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/support/tickets`);
      setTickets(data || []);
    } catch {
      setTickets([]);
    }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (form.subject.trim().length < 3) return toast.error('Konu en az 3 karakter olmalı');
    if (form.message.trim().length < 10) return toast.error('Mesajınız en az 10 karakter olmalı');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/support/tickets`, form);
      toast.success('Destek talebiniz iletildi. En kısa sürede dönüş yapacağız.');
      setForm({ subject: '', category: 'other', message: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Talep gönderilemedi');
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async () => {
    if (!activeTicket || reply.trim().length < 1) return;
    try {
      await axios.post(`${API_URL}/api/support/tickets/${activeTicket.id}/reply`, { body: reply });
      setReply('');
      // refresh detail
      const { data } = await axios.get(`${API_URL}/api/support/tickets/${activeTicket.id}`);
      setActiveTicket(data);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yanıt gönderilemedi');
    }
  };

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2" data-testid="support-page-title">
            <LifeBuoy className="w-7 h-7 text-[#2563EB]" /> Destek
          </h1>
          <p className="text-[#595959] mt-1">Bir sorun mu var? Aşağıdaki formu doldurun, ekibimiz size dönüş yapsın.</p>
        </div>

        {/* New ticket form */}
        <Card className="border border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="text-lg">Yeni Destek Talebi</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3">
                <div>
                  <Label>Konu</Label>
                  <Input
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="Kısaca sorununuzu özetleyin"
                    data-testid="support-subject"
                  />
                </div>
                <div>
                  <Label>Kategori</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="support-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k} data-testid={`support-cat-${k}`}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Mesajınız</Label>
                <Textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Sorununuzu detaylı olarak açıklayın. Ekran görüntüsü varsa info@paydaai.com adresine de ek olarak iletebilirsiniz."
                  data-testid="support-message"
                />
              </div>
              <Button type="submit" disabled={submitting} className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white gap-1" data-testid="support-submit">
                <Send className="w-4 h-4" /> {submitting ? 'Gönderiliyor…' : 'Gönder'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Ticket history */}
        <Card className="border border-[#E5E5E5]">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5" /> Geçmiş Talepleriniz
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tickets.length === 0 ? (
              <p className="text-sm text-[#595959]" data-testid="support-empty">Henüz açtığınız bir destek talebi yok.</p>
            ) : (
              <div className="space-y-2">
                {tickets.map((t) => (
                  <div key={t.id} data-testid={`support-ticket-${t.id}`} className="border border-[#E5E5E5] rounded-lg p-3 hover:bg-[#F9F8F6] cursor-pointer" onClick={() => setActiveTicket(t)}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#0F172A] truncate">{t.subject}</p>
                        <p className="text-xs text-[#595959]">
                          {CATEGORY_LABELS[t.category] || t.category} · {new Date(t.created_at).toLocaleString('tr-TR')}
                        </p>
                      </div>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_LABELS[t.status]?.color || 'bg-slate-100 text-slate-700'}`}>
                        {STATUS_LABELS[t.status]?.label || t.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active ticket modal-like detail */}
        {activeTicket && (
          <Card className="border border-[#2563EB]" data-testid="support-ticket-detail">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">{activeTicket.subject}</CardTitle>
              <Button size="sm" variant="ghost" onClick={() => setActiveTicket(null)}>Kapat</Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-[#F9F8F6] p-3 rounded-lg text-sm whitespace-pre-wrap text-[#0F172A]">
                {activeTicket.message}
              </div>
              {(activeTicket.replies || []).map((r, idx) => (
                <div key={idx} className={`p-3 rounded-lg text-sm whitespace-pre-wrap ${r.from === 'support' ? 'bg-blue-50 border border-blue-100' : 'bg-[#F9F8F6]'}`}>
                  <p className="text-[11px] text-[#595959] mb-1">
                    {r.from === 'support' ? 'Paydaai Destek' : 'Siz'} · {new Date(r.at).toLocaleString('tr-TR')}
                  </p>
                  <p className="text-[#0F172A]">{r.body}</p>
                </div>
              ))}
              {activeTicket.status !== 'closed' && (
                <div className="flex gap-2 pt-2">
                  <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Cevap yazın…" data-testid="support-reply" />
                  <Button onClick={sendReply} className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="support-reply-send">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}
              <p className="text-[11px] text-[#595959] inline-flex items-center gap-1">
                {activeTicket.status === 'resolved' || activeTicket.status === 'closed'
                  ? <CheckCircle2 className="w-3 h-3 text-green-600" />
                  : <Clock className="w-3 h-3" />}
                Son güncelleme: {new Date(activeTicket.updated_at || activeTicket.created_at).toLocaleString('tr-TR')}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
